import {
  getDocumentNotificationClientId,
  getMessageNotificationClientId,
  isDocumentOrFolderNotification,
  isMessageNotification,
} from "./notificationNavigation";
import { isStaffRole } from "./staffListScope";

export function parseAssignedEmployeeIds(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.filter(Boolean).map(String);
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed.filter(Boolean).map(String);
    } catch {
      /* fall through */
    }
    return raw
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  }
  return [];
}

/** Whether a client row is assigned to the logged-in employee/manager. */
export function isClientAssignedToStaff(client, staffUserId) {
  if (!client || staffUserId == null || staffUserId === "") return false;
  const uid = String(staffUserId);

  if (client.employee_id != null && String(client.employee_id) === uid) {
    return true;
  }
  if (client.sales != null && String(client.sales) === uid) {
    return true;
  }

  return parseAssignedEmployeeIds(client.assigned_employee_ids).some(
    (id) => String(id) === uid,
  );
}

export function buildAssignedClientIdSet(clients, staffUserId) {
  const ids = new Set();
  (clients || []).forEach((client) => {
    if (!client?.client_id) return;
    if (isClientAssignedToStaff(client, staffUserId)) {
      ids.add(String(client.client_id));
    }
  });
  return ids;
}

const CLIENT_SCOPED_NOTIFICATION_TYPES = new Set([
  "document",
  "folder",
  "message",
  "messages",
  "invoice",
  "contract",
  "payment",
  "client",
]);

function isClientScopedNotification(notify) {
  const type = String(notify?.notification_type || "").toLowerCase();
  return (
    CLIENT_SCOPED_NOTIFICATION_TYPES.has(type) ||
    isMessageNotification(notify) ||
    isDocumentOrFolderNotification(notify)
  );
}

/** Resolve the client a notification is about (for staff scoping). */
export function getNotificationRelatedClientId(notify, userRole) {
  if (!notify) return null;

  if (isMessageNotification(notify)) {
    return getMessageNotificationClientId(notify, userRole);
  }

  if (isDocumentOrFolderNotification(notify)) {
    return getDocumentNotificationClientId(notify, userRole);
  }

  const type = String(notify.notification_type || "").toLowerCase();
  if (type === "invoice" || type === "contract" || type === "payment") {
    return (
      notify.billed_to ??
      notify.client_id ??
      notify.initiated_user_id ??
      notify.notification_client_id ??
      null
    );
  }

  return (
    notify.client_id ??
    notify.notification_client_id ??
    notify.initiated_user_id ??
    notify.billed_to ??
    null
  );
}

function getNotificationTargetEmployeeId(notify) {
  if (!notify) return null;
  return (
    notify.employee_id ??
    notify.recipient_id ??
    notify.target_employee_id ??
    notify.initiated_user_id ??
    notify.notification_item_id ??
    null
  );
}

function isInternalStaffNotification(notify) {
  const type = String(notify?.notification_type || "").toLowerCase();
  return type === "leave" || type === "timesheet";
}

/**
 * Staff (employee/manager): only notifications for assigned clients,
 * plus internal leave/timesheet items for self (and team for managers).
 */
export function isNotificationVisibleToStaff(
  notify,
  { userId, userRole, assignedClientIds, teamEmployeeIds } = {},
) {
  if (!notify) return false;

  const role = String(userRole || "").toLowerCase();
  const uid = userId != null ? String(userId) : "";

  if (isClientScopedNotification(notify)) {
    const clientId = getNotificationRelatedClientId(notify, role);
    if (!clientId) return false;
    if (!assignedClientIds?.size) return false;
    return assignedClientIds.has(String(clientId));
  }

  if (isInternalStaffNotification(notify)) {
    if (String(notify.notification_type || "").toLowerCase() === "leave" && uid) {
      const addressedToViewer = [notify.notify_user_id, notify.receiver_user_id]
        .flatMap((value) => String(value || "").split(","))
        .map((value) => value.trim())
        .filter(Boolean)
        .some((value) => value.toLowerCase() === uid.toLowerCase());
      if (addressedToViewer) return true;
    }

    const targetId = getNotificationTargetEmployeeId(notify);
    if (!targetId || !uid) return false;
    if (String(targetId) === uid) return true;
    if (role === "manager" && teamEmployeeIds?.has(String(targetId))) {
      return true;
    }
    return false;
  }

  const clientId = getNotificationRelatedClientId(notify, role);
  if (clientId) {
    if (!assignedClientIds?.size) return false;
    return assignedClientIds.has(String(clientId));
  }

  const recipientId =
    notify.recipient_id ?? notify.recipient_user_id ?? notify.user_id;
  return recipientId != null && uid && String(recipientId) === uid;
}

export function filterNotificationsForUser(
  notifications,
  { userRole, userId, assignedClientIds, teamEmployeeIds, scopeReady = true } = {},
) {
  const list = Array.isArray(notifications) ? notifications : [];
  const role = String(userRole || "").toLowerCase();

  if (role === "admin" || role === "client") {
    return list;
  }

  if (!isStaffRole(role)) {
    return list;
  }

  if (!scopeReady) {
    return [];
  }

  return list.filter((item) =>
    isNotificationVisibleToStaff(item, {
      userId,
      userRole: role,
      assignedClientIds,
      teamEmployeeIds,
    }),
  );
}
